import type { Locale } from '../../../../types';
import { calculateDayDifference } from './calculateDayDifference';
import type { DateTimeFormat, DateTimeSettings, DateTimeFormatterInstancesMap } from './types';


const createFriendlyUtils = (
    locale: Locale,
    settings: DateTimeSettings,
    formatters: DateTimeFormatterInstancesMap
) => {
    const { timeZone, friendlyModeMinOffset, friendlyModeMaxOffset } = settings;

    // utility Intl formatters
    const rtf = new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto'
    });

    const intlTarget = new Intl.DateTimeFormat(locale, {
        timeZone,
        month: 'long',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
    });

    const intlPlaceholder = formatters.month;

    // methods
    const createFriendlyDateFormatter = (dayDiff: number) => ({
        format: (_: Date): string => {
            return rtf.format(dayDiff, 'day');
        }
    });
    
    const createFriendlyDateTimeFormatter = (dayDiff: number) => ({
        format: (date: Date): string => {
            const target = intlTarget.format(date);
    
            return target.replace(
                intlPlaceholder.format(date),
                createFriendlyDateFormatter(dayDiff).format(date)
            );
        }
    });

    const getFriendlyFormatter = (date: Date, format: DateTimeFormat) => {
        const dayDiff = calculateDayDifference(date, settings);

        // Check if within friendly mode range
        if (dayDiff < friendlyModeMinOffset || dayDiff > friendlyModeMaxOffset) {
            return formatters[format];
        }

        // Return appropriate friendly formatter
        if (format.includes('dateTime')) {
            return createFriendlyDateTimeFormatter(dayDiff);
        }
        
        if (format.includes('date')) {
            return createFriendlyDateFormatter(dayDiff);
        }

        return formatters[format];
    };

    return {
        calculateDayDifference,
        getFriendlyFormatter,
    };
}

export {
    createFriendlyUtils    
}
